// Mounting an Apex chart into a report surface. One place decides how tall the chart may be (after the title,
// notes and controls are in place, so the legend is never clipped), redraws it when its container is resized
// (dashboard tile resize, density toggle, full screen) or the theme changes, and keeps the instance reachable
// for image export.

import { onThemeChange, readChartTheme, type ChartTheme } from './chart-theme';

export interface ApexInstance {
    render(): Promise<void>;
    destroy(): void;
    updateOptions?(options: unknown, redraw?: boolean, animate?: boolean): Promise<void>;
    dataURI?(options?: { scale?: number; width?: number }): Promise<{ imgURI?: string; blob?: Blob }>;
    ctx?: { exports?: { getSvgString?(scale?: number): string } };
}

declare global {
    interface Window {
        ApexCharts: new (el: HTMLElement, options: unknown) => ApexInstance;
    }
}

let apexReady: Promise<typeof window.ApexCharts> | null = null;

/** Shared wait for the deferred CDN script; all chart paints should use this. */
export function awaitApex(timeoutMs = 20_000): Promise<typeof window.ApexCharts> {
    if (typeof window !== 'undefined' && typeof window.ApexCharts !== 'undefined') return Promise.resolve(window.ApexCharts);
    if (!apexReady) {
        apexReady = new Promise((resolve, reject) => {
            const started = Date.now();
            const tick = (): void => {
                if (typeof window !== 'undefined' && typeof window.ApexCharts !== 'undefined') { resolve(window.ApexCharts); return; }
                if (Date.now() - started >= timeoutMs) { apexReady = null; reject(new Error('ApexCharts not available')); return; }
                setTimeout(tick, 50);
            };
            tick();
        });
    }
    return apexReady;
}

/** Live chart instances by their mount element, for image export. */
const instances = new WeakMap<Element, ApexInstance>();
export function chartInstance(element: Element): ApexInstance | undefined { return instances.get(element); }

/** Below this a chart cannot draw anything legible; above it, the chart always fits its container. */
export const MIN_CHART_HEIGHT = 64;

function outerHeight(element: Element): number {
    if (!(element instanceof HTMLElement)) return 0;
    const style = getComputedStyle(element);
    if (style.position === 'absolute' || style.position === 'fixed' || style.display === 'none') return 0;
    return element.offsetHeight + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
}

function px(value: string): number | null {
    const n = parseFloat(value);
    return value.endsWith('px') && Number.isFinite(n) ? n : null;
}

/** Height a host gives a chart when it does not size the chart itself (auto-height containers). */
export function fallbackChartHeight(host: HTMLElement): number {
    const widget = host.closest<HTMLElement>('.analytics-widget');
    if (widget) {
        const spanMatch = widget.style.gridRow.match(/span\s+(\d+)/);
        const rows = spanMatch ? parseInt(spanMatch[1], 10) : 3;
        return Math.max(MIN_CHART_HEIGHT, rows * 80 - 96);
    }
    return 320;
}

/** A measurement probe, never painted: tall enough to show whether the host grows with its content. */
const GROWTH_PROBE_HEIGHT = 2000;

/**
 * The height left for `slot` inside `host` once everything else in the host (title, notes, navigation,
 * drill controls) has taken its share. A host whose height follows its content gets a fallback height.
 */
export function availableChartHeight(host: HTMLElement, slot: HTMLElement, fallback = fallbackChartHeight(host)): number {
    if (typeof getComputedStyle !== 'function') return fallback;
    const style = getComputedStyle(host);
    const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
    const slotStyle = getComputedStyle(slot);
    const slotMargins = (parseFloat(slotStyle.marginTop) || 0) + (parseFloat(slotStyle.marginBottom) || 0);
    const others = [...host.children].filter(child => child !== slot && !child.contains(slot)).reduce((sum, child) => sum + outerHeight(child), 0);
    // Probe: does the host grow with its content? Restore inline sizes afterwards.
    const saved = [slot.style.height, slot.style.minHeight, slot.style.flex];
    slot.style.minHeight = '0'; slot.style.flex = '0 0 auto';
    slot.style.height = '0'; const collapsed = host.clientHeight;
    slot.style.height = `${GROWTH_PROBE_HEIGHT}px`; const expanded = host.clientHeight;
    [slot.style.height, slot.style.minHeight, slot.style.flex] = saved;
    if (collapsed > 8 && expanded - collapsed < 40) return Math.max(MIN_CHART_HEIGHT, Math.floor(collapsed - padding - others - slotMargins));
    const max = px(style.maxHeight);
    if (max != null) return Math.max(MIN_CHART_HEIGHT, Math.min(fallback, Math.floor(max - padding - others - slotMargins)));
    return fallback;
}

export interface MountContext { height: number; width: number; theme: ChartTheme }

export interface MountOptions {
    /** The element whose size the chart follows; usually the renderChart host. */
    host: HTMLElement;
    /** The child of `host` the chart lives in (the mount element itself, or a page wrapper around it). */
    slot?: HTMLElement;
    /** A fixed height, for hosts that size their pieces themselves (pages, panels). */
    height?: () => number;
    label: string;
    /** Element whose resizes trigger a redraw (defaults to host). */
    observe?: HTMLElement;
    /** Called after each successful draw. */
    onRendered?: (chart: ApexInstance) => void;
}

export interface MountedChart { destroy(): void; redraw(): void }

/**
 * Mount an Apex chart into `element`, building its options from the measured height and the current theme.
 * Redraws when the host is resized or the theme changes; `destroy` releases observers and the instance.
 */
export function mountApex(element: HTMLElement, build: (context: MountContext) => Record<string, unknown>, options: MountOptions): MountedChart {
    let chart: ApexInstance | null = null;
    let disposed = false; let drawing = false; let again = false;
    let lastHeight = -1; let lastWidth = -1; let themeKey = '';
    let timer = 0;
    element.dataset.analyticsChartMount = '';
    const measure = () => options.height?.() ?? availableChartHeight(options.host, options.slot ?? element);
    const draw = (force: boolean): void => {
        if (disposed) return;
        // A host that replaced its content without calling destroy must not keep a detached chart alive.
        if (lastHeight >= 0 && !element.isConnected) { handle.destroy(); return; }
        if (drawing) { again = again || force; return; }
        const theme = readChartTheme(); const key = JSON.stringify(theme);
        const height = measure(); const width = element.clientWidth;
        if (!force && chart && Math.abs(height - lastHeight) < 4 && key === themeKey) return;
        drawing = true;
        void awaitApex().then(Apex => {
            if (disposed) return;
            const config = build({ height, width, theme });
            // Builders return chart events beside the options; Apex reads them from chart.events.
            if (config.chartEvents) { (config.chart as Record<string, unknown>).events = config.chartEvents; }
            delete config.chartEvents;
            chart?.destroy(); instances.delete(element);
            element.replaceChildren();
            const next = new Apex(element, config); chart = next;
            lastHeight = height; lastWidth = width; themeKey = key;
            return next.render().then(() => {
                if (disposed || chart !== next) return;
                instances.set(element, next);
                // Axis crosshair tooltips are disabled, but Apex still parks hidden boxes below the plot, which
                // would make the container scroll. Remove them from layout.
                element.querySelectorAll<HTMLElement>('.apexcharts-xaxistooltip, .apexcharts-yaxistooltip').forEach(tip => { tip.style.display = 'none'; });
                element.setAttribute('role', 'img'); element.setAttribute('aria-label', options.label);
                if (!element.hasAttribute('tabindex')) element.tabIndex = 0;
                options.onRendered?.(next);
            });
        }).catch(error => {
            if (disposed) return;
            console.warn('analytics-charts: chart unavailable', error);
            element.textContent = 'Chart unavailable. Use the data table.';
        }).finally(() => {
            drawing = false;
            if (again && !disposed) { again = false; draw(true); }
        });
    };
    const schedule = (): void => {
        if (disposed) return;
        clearTimeout(timer);
        timer = setTimeout(() => {
            if (disposed) return;
            if (!element.isConnected) { handle.destroy(); return; }
            const height = measure(); const width = element.clientWidth;
            // Apex follows width itself; a new height (or a first width after being hidden) needs a redraw.
            if (Math.abs(height - lastHeight) >= 4 || (lastWidth <= 0 && width > 0)) draw(true);
        }, 120) as unknown as number;
    };
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
    resize?.observe(options.observe ?? options.host);
    // Theme changes arrive through the module-level observer.
    const unsubscribe = onThemeChange(() => draw(true));
    const handle: MountedChart = {
        redraw: () => draw(true),
        destroy() {
            disposed = true; clearTimeout(timer); resize?.disconnect(); unsubscribe();
            instances.delete(element); chart?.destroy(); chart = null;
        },
    };
    draw(true);
    return handle;
}

/** Every chart drawn inside `root`, in document order. */
export function chartsWithin(root: Element): ApexInstance[] {
    const mounts = [...(root.matches?.('[data-analytics-chart-mount]') ? [root] : []), ...root.querySelectorAll('[data-analytics-chart-mount]')];
    return mounts.map(m => instances.get(m)).filter((c): c is ApexInstance => !!c);
}
